import React, { useEffect, useState } from "react";
import cx from "classnames";
import styles from "../scss/DapQuestion.module.scss";
import { ControlType } from "../enums/ControlType";
import ManageDependentChild from "./ManageDependentChild";
import CheckboxListField from "./CheckboxListField";
import SubQuestionCurrency from "./SubQuestionCurrency";
import Occupation from "../../common/components/Forms/Occupation/Occupation";
import Button from "../../common/components/Forms/Button/Button";
import Icon from "../../common/components/Icon/Icon";
import Input from "../../common/components/Forms/Input/Input";
import Dropdown from "../../common/components/Forms/Dropdown/Dropdown";
import { IDigitalQuestion, IQuestionOptionImage } from "../interfaces/IDigitalAdviceData";
import { IDropdownOption } from "../../common/interfaces/IDropdownOption";
import { createRange, getOptionImages } from "../shared/digitalAdviceHelper";
import { ISumbitParams } from "./DigitalAdviceQuestion";
import DAQuestionFieldImage from "./DAQuestionFieldImage";
import { Form, Formik } from "formik";

interface IDAQuestionFieldProps {
    question: IDigitalQuestion;
    fieldReady: (status: boolean) => void;
    onSubmit: (params: ISumbitParams) => Promise<void>;
    onMultiSubmit: (answerList: ISumbitParams[]) => Promise<void>;
    addFrameImage?: (image: IQuestionOptionImage, isAdded: boolean) => void;
    addMultipleFrameImages?: (images: IQuestionOptionImage[], isAdded: boolean) => void;
    setImagePlaceholders?: (images: IQuestionOptionImage[]) => void;
    resetFramePlaceholder?: () => void;
}

const DAQuestionField: React.FC<IDAQuestionFieldProps> = (props) => {
    const { question, onSubmit, onMultiSubmit } = props;

    const [value, setValue] = useState("");
    const [isSubmit, setIsSubmit] = useState(false);
    const [valueNumber, setValueNumber] = useState<number>();
    const [occupation, setOccupation] = useState({
        label: "",
        value: ""
    });
    const [subQuestions, setSubQuestions] = useState<IDigitalQuestion[]>();
    const [emailError, setEmailError] = useState(null);
    const [emailTouch, setEmailTouch] = useState(false);

    const getAgeOptions = (start: number, end: number): IDropdownOption[] => {
        return createRange(start, end).map((i) => ({
            value: i.toString(),
            label: i.toString()
        }));
    };
    const ageOptions = [{ value: "", label: "" }, ...getAgeOptions(18, 65)];

    const handleResetForm = (): void => {
        setValue("");
        setValueNumber(null);
        setOccupation({
            label: "",
            value: ""
        });
        setIsSubmit(false);
    };

    const handleSubmit = (answer): void => {
        if (isSubmit) {
            return;
        }
        setIsSubmit(true);
        onSubmit(answer).finally(() => handleResetForm());
    };

    const handleMultiSubmit = (answerList): void => {
        if (isSubmit) {
            return;
        }
        setIsSubmit(true);
        onMultiSubmit(answerList).finally(() => handleResetForm());
    };

    const getSubquestionControlType = (): number => {
        return (subQuestions?.length && subQuestions[0]?.controlTypeId) || 0;
    };

    const isValidEmail = (email) => {
        return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email);
    };

    useEffect(() => {
        setSubQuestions(question.subQuestions);
        if (question.id) {
            props.fieldReady(true);
        }
    }, [question.id]);

    return (
        <div className={styles["question-field"]}>
            {question.controlTypeId === ControlType.PLACEHOLDER &&
                getSubquestionControlType() === ControlType.AGE && (
                    <ManageDependentChild
                        onSubmit={handleMultiSubmit}
                        subQuestionList={question.subQuestions}
                        addMultipleFrameImages={props.addMultipleFrameImages}
                        setImagePlaceholders={props.setImagePlaceholders}
                        resetFramePlaceholder={props.resetFramePlaceholder}
                    />
                )}
            {question.controlTypeId === ControlType.PLACEHOLDER &&
                getSubquestionControlType() === ControlType.CHECKBOX && (
                    <CheckboxListField
                        onSubmit={handleMultiSubmit}
                        list={question.subQuestions}
                        isPlaceholderField={true}
                        addFrameImage={props.addFrameImage}
                    />
                )}
            {question.controlTypeId === ControlType.PLACEHOLDER &&
                getSubquestionControlType() === ControlType.CURRENCY && (
                    <SubQuestionCurrency
                        onSubmit={handleMultiSubmit}
                        list={question.subQuestions}
                    />
                )}
            {question.controlTypeId === ControlType.OCCUPATION && (
                <Formik
                    initialValues={{}}
                    onSubmit={() => {
                        if (isSubmit || !(occupation && occupation.value)) return;
                        handleSubmit({
                            questionId: question.id,
                            textValue: JSON.stringify(occupation)
                        });
                    }}>
                    {({ handleBlur }) => (
                        <Form
                            className={cx([
                                "layout-flex-column--centered",
                                styles["field-occupation-input-container"]
                            ])}>
                            <div className={styles["field-occupation-input"]}>
                                <Occupation
                                    id={`occupationFieldName`}
                                    title={" "}
                                    className={styles["field-occupation-input--style"]}
                                    placeholder={question?.placeholder}
                                    initialValue={{
                                        label: occupation?.label,
                                        value: occupation?.value
                                    }}
                                    onSelect={(e) => {
                                        props.addFrameImage(
                                            getOptionImages(question),
                                            true
                                        );
                                        setOccupation({
                                            label: e.label,
                                            value: e.value
                                        });
                                    }}
                                    onBlur={handleBlur}
                                />
                            </div>

                            <div className={styles.buttonNext}>
                                <Button
                                    className={cx("get-started")}
                                    width="content"
                                    component="button"
                                    size="m"
                                    type="submit"
                                    isDisabled={
                                        isSubmit || !(occupation && occupation.value)
                                    }
                                    variant="calltoaction">
                                    <span>Next</span>
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            )}
            {question.controlTypeId === ControlType.TEXTFIELD && (
                <Formik
                    initialValues={{}}
                    onSubmit={() => {
                        if (isSubmit || !value) return;
                        handleSubmit({
                            questionId: question.id,
                            textValue: value
                        });
                    }}>
                    {() => (
                        <Form className={cx("layout-flex-column--centered")}>
                            <div className={styles["field-input"]}>
                                <Input
                                    name="controlTypeField"
                                    type="email"
                                    value={value}
                                    onChange={(e) => {
                                        setValue(e.target.value);
                                    }}
                                    placeholder={question?.placeholder}></Input>
                            </div>

                            <div className={styles.buttonNext}>
                                <Button
                                    className={cx("get-started")}
                                    width="content"
                                    component="button"
                                    size="m"
                                    type="submit"
                                    isDisabled={isSubmit || !value}
                                    variant="calltoaction">
                                    <span>Next</span>
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            )}
            {question.controlTypeId === ControlType.NUMBER && (
                <Formik
                    initialValues={{}}
                    onSubmit={() => {
                        if (isSubmit || !(valueNumber >= 0)) return;
                        handleSubmit({
                            questionId: question.id,
                            textValue: valueNumber
                        });
                    }}>
                    {() => (
                        <Form className={cx("layout-flex-column--centered")}>
                            <div className={styles["field-number-input"]}>
                                <Input
                                    name="controlTypeNumber"
                                    type="number"
                                    hasCurrency={true}
                                    value={valueNumber}
                                    onChange={setValueNumber}
                                    placeholder={question?.placeholder}></Input>
                            </div>

                            <div className={styles.buttonNext}>
                                <Button
                                    className={cx("get-started")}
                                    width="content"
                                    component="button"
                                    size="m"
                                    type="submit"
                                    isDisabled={isSubmit || !(valueNumber >= 0)}
                                    variant="calltoaction">
                                    <span>Next</span>
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            )}
            {question.controlTypeId === ControlType.AGE && (
                <Formik
                    initialValues={{}}
                    onSubmit={() => {
                        if (isSubmit || !value || value === "0") return;
                        handleSubmit({
                            questionId: question.id,
                            textValue: value
                        });
                    }}>
                    {() => (
                        <Form
                            className={cx("layout-flex-column--centered")}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleSubmit({
                                        questionId: question.id,
                                        textValue: value
                                    });
                                }
                            }}>
                            <div className={styles["field-dropdown"]}>
                                <Dropdown
                                    id={`dropdown`}
                                    name={`dropdown`}
                                    options={ageOptions}
                                    selectedOptionId={value}
                                    onChange={(e) => {
                                        setValue(e.target.value);
                                    }}
                                    variant={"default"}
                                    className={styles["field-dropdown--style"]}
                                />
                            </div>
                            <div className={styles.buttonNext}>
                                <Button
                                    className={cx("get-started")}
                                    width="content"
                                    component="button"
                                    size="m"
                                    type="submit"
                                    isDisabled={isSubmit || !value || value === "0"}
                                    variant="calltoaction">
                                    <span>Next</span>
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            )}
            {question.controlTypeId === ControlType.EMAIL && (
                <Formik
                    initialValues={{}}
                    onSubmit={() => {
                        if (emailError || isSubmit || !value) return;
                        handleSubmit({
                            questionId: question.id,
                            textValue: value
                        });
                    }}>
                    {() => (
                        <Form className={cx("layout-flex-column--centered")}>
                            <div className={styles["field-input"]}>
                                <Input
                                    name="controlTypeEmail"
                                    type="email"
                                    value={value}
                                    onChange={(e) => {
                                        if (!isValidEmail(e.target.value)) {
                                            setEmailError("Please enter a valid email");
                                        } else {
                                            setEmailError(null);
                                        }

                                        setValue(e.target.value);
                                    }}
                                    onBlur={() => {
                                        setEmailTouch(true);
                                    }}
                                    hasError={emailTouch && emailError}
                                    placeholder={question?.placeholder}></Input>
                            </div>

                            <div className={styles.buttonNext}>
                                <Button
                                    className={cx("get-started")}
                                    width="content"
                                    component="button"
                                    size="m"
                                    type="submit"
                                    isDisabled={emailError || isSubmit || !value}
                                    variant="calltoaction">
                                    <span>Next</span>
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            )}
            {question.controlTypeId === ControlType.RADIOBUTTON && (
                <div className={styles["field-radio-button"]}>
                    {question.options.map((opt, idx) => (
                        <div className={styles["radio-button"]} key={idx}>
                            <input
                                type="radio"
                                id={`radiocontrol-${idx}`}
                                name="radioControl"
                                value={opt.responseValue}
                                checked={value === opt.responseValue}
                                onChange={(e) => {
                                    setValue(e.target.value);
                                }}
                                onClick={() => {
                                    props.addFrameImage(getOptionImages(opt), true);
                                    props.setImagePlaceholders(
                                        question.nextQuestionPlaceholderImages
                                    );
                                    handleSubmit({
                                        questionId: question.id,
                                        questionOptionId: opt.id
                                    });
                                }}
                                disabled={isSubmit}
                            />
                            <label
                                className={styles["radion-button--label-container"]}
                                htmlFor={`radiocontrol-${idx}`}>
                                <div className={styles["img-container"]}>
                                    <DAQuestionFieldImage imgUrl={opt.imgUrl} />
                                </div>
                                <p className={styles["rb--label"]}>{opt.label}</p>
                                <div className={styles["rb--checkicon"]}></div>
                            </label>
                        </div>
                    ))}
                </div>
            )}
            {question.controlTypeId === ControlType.CHECKBOX && (
                <CheckboxListField
                    questionId={question.id}
                    maxWidth={"unset"}
                    onSubmit={handleMultiSubmit}
                    list={question.options}
                    addFrameImage={props.addFrameImage}
                />
            )}
        </div>
    );
};

export default DAQuestionField;
